/**
 * libraryDoctypeVocabulary — pure registry that maps a library's
 * TARGET output doctype (the regulatory document the library will
 * feed) to the set of SOURCE document types that should appear in
 * the LibraryHub doc-type dropdown for documents in that library.
 *
 * Why this exists (Phase 9.3):
 *   The pre-Phase-9 LibraryHub dropdown showed a flat 17-item list
 *   of OUTPUT doctypes (CSR / PSUR / CER / IB / SmPC / …). Writers
 *   uploading common CSR exhibits (AE tables, demographics tables,
 *   efficacy results listings) had no real option and were forced
 *   into "Other" with low confidence. Per-library narrowing — once
 *   we know "this library feeds a CSR" — surfaces the right
 *   vocabulary so the writer can classify accurately.
 *
 * SRP: pure data + one tiny derive helper. No React, no I/O, no
 * persistence. The store decides which target_doctype is set;
 * this module decides what options to show given that target.
 *
 * # DECISION: target doctypes are a CLOSED enum (``CSR | PSUR | CER
 * #   | IB``) for v1. Adding a target requires touching this module
 * #   AND the Hub's picker — TypeScript enforces it via the
 * #   exhaustive ``Record<TargetDoctype, ...>`` shape below.
 * # WHY: Q1 (2026-04-27) — agreed scope is the four ICH/MDR-aligned
 * #      regulatory output types. Open-string targets would mean
 * #      every library could declare a novel target with no
 * #      vocabulary registered, defeating the purpose. Closed enum
 * #      keeps the contract honest; expanding is a deliberate code
 * #      change with a vocabulary update in the same commit.
 * # DATE: 2026-04-27
 *
 * # DECISION: every target's vocabulary INCLUDES ``"Other"`` and
 * #   ``"Custom…"`` as sentinels.
 * # WHY: writer-confirmed (2026-04-27) — "We should still retain
 * #      the Custom option for the writer to write. There will
 * #      definitely be edge cases which we can account for with the
 * #      Custom option." Same goes for Other — the registry is
 * #      narrowed by intent, not by completeness; writers keep their
 * #      escape hatches. The Custom sentinel value is shared with
 * #      LibraryHub's existing ``DOC_TYPE_CUSTOM_SENTINEL`` so the
 * #      freetext flow continues to work unchanged.
 * # DATE: 2026-04-27
 *
 * # DECISION: CSR vocabulary is comprehensive + data-grounded; the
 * #   PSUR / CER / IB lists are STARTER sets explicitly marked for
 * #   expansion as writer feedback arrives.
 * # WHY: Q2 (2026-04-27) — agent scan grounded the CSR list in this
 * #      repo's L0 filename classifier patterns + the
 * #      ``RTF_TABLE_MAPPINGS`` table catalog. The other targets
 * #      have much thinner coverage in the codebase today; rather
 * #      than invent options, we ship deliberate starters and grow
 * #      from real uploads.
 * # DATE: 2026-04-27
 */

// ---------------------------------------------------------------------------
// Closed enum + types
// ---------------------------------------------------------------------------

/** The library's target output doctype — what the library feeds. */
export const TARGET_DOCTYPES = ["CSR", "PSUR", "CER", "IB", "CMC"] as const;
export type TargetDoctype = (typeof TARGET_DOCTYPES)[number];

/** Predicate exported for parse paths (e.g. localStorage rehydrate)
 *  that need to validate a string before narrowing it to TargetDoctype. */
export function isTargetDoctype(value: unknown): value is TargetDoctype {
  return typeof value === "string" && (TARGET_DOCTYPES as readonly string[]).includes(value);
}

/** Sentinel values shared with LibraryHub's existing freetext flow.
 *  Re-exported here so both modules read from one source rather than
 *  duplicating the strings; LibraryHub already owns the actual
 *  ``DOC_TYPE_CUSTOM_SENTINEL`` UI behaviour. */
export const DOCTYPE_OTHER = "Other";
export const DOCTYPE_CUSTOM_LABEL = "Custom…";

// ---------------------------------------------------------------------------
// Per-target vocabularies
// ---------------------------------------------------------------------------

/** Foundational source-document types — appear in EVERY target's
 *  vocabulary because every regulatory output cites a Protocol,
 *  SAP, etc. Defined once so the per-target lists DRY against it. */
const FOUNDATIONAL_SOURCES: readonly string[] = ["Protocol", "SAP", "CRF", "IB", "ICF"];

/** CSR-specific table types — derived from
 *  ``src/csr_sim/library_resolution/strategies/filename_heuristic.py``
 *  L0 patterns + ``src/csr_sim/config.py`` RTF_TABLE_MAPPINGS. The
 *  granular tables matter when writers upload exhibits separately;
 *  ``TLF`` covers the bundled-artifact case. Order is roughly
 *  reading-order-in-a-CSR (disposition → demographics → efficacy →
 *  safety → exposure → other) so the dropdown reads top-to-bottom
 *  the way a writer thinks about §13 / §14. */
const CSR_TABLES: readonly string[] = [
  "TLF",
  "Subject Disposition Table",
  "Demographics and Baseline Characteristics Table",
  "Primary Efficacy Results Table",
  "Secondary Efficacy Results Table",
  "Treatment-Emergent Adverse Events Table",
  "Serious Adverse Events Table",
  "Adverse Events Leading to Discontinuation Table",
  "Grade 3+ TEAE Table",
  "Immune-Related Adverse Events Table",
  "Concomitant Medications Table",
  "Prior Therapy Listing",
  "Exposure Summary Table",
  "Protocol Deviations Table",
  "Laboratory Parameters Table",
  "Vital Signs Table",
  "Pharmacokinetic Parameters Table",
];

/** Closing sentinels — appended last on every target's list so
 *  the writer's escape hatches are always at the bottom of the
 *  dropdown (consistent positional muscle memory). */
const CLOSING_SENTINELS: readonly string[] = [DOCTYPE_OTHER, DOCTYPE_CUSTOM_LABEL];

/**
 * Per-target source vocabularies. Exported as a closed
 * ``Record<TargetDoctype, ...>`` so adding a new TargetDoctype
 * fails to compile here — TypeScript catches the missing entry
 * before anything else notices.
 *
 * IFU is included in CSR specifically for device-targeted CSRs
 * (combination products); negligible noise for pharma-only writers
 * who'll just skip it.
 */
export const LIBRARY_SOURCE_VOCABULARY: Record<TargetDoctype, ReadonlyArray<string>> = {
  // CSR — comprehensive + data-grounded.
  CSR: [...FOUNDATIONAL_SOURCES, "IFU", ...CSR_TABLES, ...CLOSING_SENTINELS],
  // PSUR / PBRER — STARTER set; expand as writers upload real
  // pharmacovigilance materials. ICSR listings + signal-detection
  // outputs are the obvious next adds; held back until we see them.
  PSUR: [
    ...FOUNDATIONAL_SOURCES,
    "PBRER",
    "DSUR",
    "Treatment-Emergent Adverse Events Table",
    "Serious Adverse Events Table",
    "Cumulative Exposure Summary",
    "Signal Detection Summary",
    ...CLOSING_SENTINELS,
  ],
  // CER (device CER, MDR / MEDDEV 2.7/1) — STARTER set; expand as
  // writers upload PMCFR / PMCFP / device-class evidence.
  CER: [
    ...FOUNDATIONAL_SOURCES,
    "IFU",
    "RMF",
    "Clinical Evaluation Plan",
    "PMCFR",
    "PMCFP",
    "Literature Search Report",
    "Equivalence Assessment",
    ...CLOSING_SENTINELS,
  ],
  // IB (Investigator's Brochure) — STARTER set; the IB itself is
  // foundational so the foundational list already covers most of
  // what feeds it. Add nonclinical / CMC summaries as they show up.
  IB: [
    ...FOUNDATIONAL_SOURCES,
    "Nonclinical Study Report",
    "CMC Summary",
    "Pharmacology Summary",
    "Toxicology Summary",
    "Prior Clinical Experience Summary",
    ...CLOSING_SENTINELS,
  ],
  // CMC Module 3 (small molecule, ICH M4Q + Q-series) — STARTER
  // set derived from the cmc_small_molecule.yaml preamble. Covers
  // the core API + DP source families a writer typically uploads
  // before drafting Module 3. Expand as writers upload real
  // submission packages and we see what's missing.
  //
  // Foundational sources (Protocol/SAP/CRF/IB/ICF) intentionally
  // omitted — those don't feed Module 3 quality content. CMC
  // libraries are quality-doc-centric; including foundational
  // entries would just be noise in the dropdown.
  CMC: [
    "API Development Report",
    "Drug Product Development Report",
    "Executed Batch Records",
    "Batch Analysis",
    "Stability Study Report",
    "Analytical Method Validation",
    "Process Validation Report",
    "Specifications (API)",
    "Specifications (Drug Product)",
    "Container Closure Qualification",
    "Excipient Information",
    "DMF Letter of Authorization",
    "Elemental Impurities Risk Assessment",
    "Nitrosamines Risk Assessment",
    "CMC Project Specification",
    "Regulatory Assessment Report",
    ...CLOSING_SENTINELS,
  ],
};

// ---------------------------------------------------------------------------
// Public derive helper — the only thing LibraryHub needs to call
// ---------------------------------------------------------------------------

/**
 * Resolve the dropdown option list for a library given its current
 * target doctype.
 *
 *   * ``targetDoctype === null`` (legacy library, writer hasn't
 *     picked a target yet) → return the full ``fallback`` list
 *     unchanged. Honest "we don't know what to narrow to."
 *   * ``targetDoctype`` set → return the registry entry for that
 *     target. Always includes ``Other`` + ``Custom…`` so the
 *     writer's escape hatches stay reachable.
 *
 * Pure; no React, no I/O. The fallback parameter exists so
 * LibraryHub can pass its existing ``DOC_TYPE_OPTIONS`` (the
 * pre-Phase-9 list) without this module having to know about it.
 */
export function deriveDocTypeOptions(
  targetDoctype: TargetDoctype | null,
  fallback: ReadonlyArray<string>,
): ReadonlyArray<string> {
  if (targetDoctype === null) return fallback;
  return LIBRARY_SOURCE_VOCABULARY[targetDoctype];
}
